import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ChevronRight, ChevronsLeftRight, ChevronsRightLeft, ClipboardList, Download, FileSpreadsheet, Footprints, IndianRupee, Minus, RefreshCw, TrendingDown, TrendingUp, Users2 } from "lucide-react";
import { EmptyState, KpiCard, PageHeader } from "../../components/common";
import { DateRangePicker } from "../../components/DateRangePicker";
import { TableRowsSkeleton } from "../../components/PaginationBar";
import { TerritoryFilterSelects, territoryLabel, territoryParams, useTerritoryMasters, EMPTY_TERRITORY } from "../../components/TerritoryFilter";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Label } from "../../components/ui/label";
import { Skeleton } from "../../components/ui/skeleton";
import { Switch } from "../../components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";
import { api, downloadBlob, errMsg, fmtDate, fmtINR } from "../../lib/api";
import { presetRange, rangeLabel } from "../../lib/dateRange";
import { useRoleModules } from "../../lib/roleModules";

/**
 * Phase 180 / 181 — Supervisor Report (Admin → Reports → Supervisor Report).
 * Four metrics per supervisor — Total Visits, Total Collections (₹ + receipts), New Assessments, Reassessments — pulled
 * from the wards / mohallas themselves (property location), so wards without a collector and receipts without a
 * collector still count. Rows expand Supervisor → Ward → Mohalla; the lowest level (a mohalla, or a ward without
 * mohallas) opens a detail panel with collector-wise figures and every receipt. "Compare with previous period" (on by
 * default) loads the mirrored previous period; the Previous and Change columns stay HIDDEN until the small ⇔ toggle
 * beside a metric's group header is clicked — per metric group, and the choice applies to every drill-down level
 * (supervisor, ward, mohalla and total rows alike). Phase 182.
 */

const n = (v) => Number(v || 0).toLocaleString("en-IN");
const METRICS = [
  { key: "visits", label: "Total Visits", money: false },
  { key: "collections_amount", label: "Total Collections", money: true },
  { key: "new_assessments", label: "New Assessments", money: false },
  { key: "reassessments", label: "Reassessments", money: false },
];
/** Phase 182 — default: only the Current column of every metric group is visible. */
const ALL_COLLAPSED = Object.fromEntries(METRICS.map((m) => [m.key, false]));
const ALL_EXPANDED = Object.fromEntries(METRICS.map((m) => [m.key, true]));
/** Table min-width by number of metric groups showing their Previous + Change columns (keeps cells readable before scrolling kicks in). */
const MIN_W = ["", "min-w-[1000px]", "min-w-[1100px]", "min-w-[1200px]", "min-w-[1280px]"];

const Metric = ({ value, testId, muted }) => <span className={`tabular-nums ${muted ? "text-muted-foreground" : ""}`} data-testid={testId}>{n(value)}</span>;

const Money = ({ amount, count, testId, muted }) => (
  <div className={`leading-tight ${muted ? "text-muted-foreground" : ""}`} data-testid={testId}>
    <div className={`tabular-nums ${muted ? "" : "font-medium"}`}>{fmtINR(amount || 0)}</div>
    <div className="text-[11px] text-muted-foreground tabular-nums">{n(count)} receipt{Number(count) === 1 ? "" : "s"}</div>
  </div>
);

/** Phase 181 — the "Change" cell: ▲ / ▼ with the absolute difference and % vs the previous period. */
const Change = ({ change, money = false, countChange, testId }) => {
  const abs = Number(change?.abs || 0);
  const pct = change?.pct;
  const Icon = abs > 0 ? TrendingUp : abs < 0 ? TrendingDown : Minus;
  const tone = abs > 0 ? "text-emerald-700 dark:text-emerald-400" : abs < 0 ? "text-rose-700 dark:text-rose-400" : "text-muted-foreground";
  const sign = abs > 0 ? "+" : abs < 0 ? "−" : "";
  const absTxt = money ? `${sign}${fmtINR(Math.abs(abs))}` : `${sign}${n(Math.abs(abs))}`;
  const pctTxt = pct == null ? (abs ? "new" : "—") : `${pct > 0 ? "+" : ""}${pct}%`;
  return (
    <div className={`leading-tight tabular-nums ${tone}`} data-testid={testId}>
      <div className="flex items-center justify-end gap-1 text-sm"><Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />{absTxt}</div>
      <div className="text-[11px] opacity-80">
        {pctTxt}
        {money && countChange != null && <> · {countChange > 0 ? "+" : countChange < 0 ? "−" : ""}{n(Math.abs(countChange))} rcpt</>}
      </div>
    </div>
  );
};

/** One metric → 1 cell (group collapsed / compare off) or 3 cells Current | Previous | Change (group expanded). */
const MetricCells = ({ node, metric, expanded, testPrefix, size = "" }) => {
  const cur = metric.money
    ? <Money amount={node.collections_amount} count={node.collections_count} testId={testPrefix ? `${testPrefix}-collections` : undefined} />
    : <Metric value={node[metric.key]} testId={testPrefix ? `${testPrefix}-${metric.key}` : undefined} />;
  if (!expanded) return <TableCell className={`text-right ${size}`}>{cur}</TableCell>;
  const prev = node.prev || {};
  return (
    <>
      <TableCell className={`text-right ${size}`}>{cur}</TableCell>
      <TableCell className={`text-right ${size} bg-muted/20`}>
        {metric.money ? <Money amount={prev.collections_amount} count={prev.collections_count} muted testId={testPrefix ? `${testPrefix}-collections-prev` : undefined} />
          : <Metric value={prev[metric.key]} muted testId={testPrefix ? `${testPrefix}-${metric.key}-prev` : undefined} />}
      </TableCell>
      <TableCell className={`text-right ${size}`}>
        <Change change={node.change?.[metric.key]} money={metric.money} countChange={metric.money ? node.change?.collections_count?.abs : null}
          testId={testPrefix ? `${testPrefix}-${metric.key}-change` : undefined} />
      </TableCell>
    </>
  );
};

const Caret = ({ open, hidden }) => (
  <ChevronRight aria-hidden="true" className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${open ? "rotate-90" : ""} ${hidden ? "invisible" : ""}`} />
);

/** Phase 182 — the small ⇔ / ⇒⇐ icon beside a metric group header: shows / hides that group's Previous + Change columns. */
const MetricToggle = ({ metric, expanded, onToggle }) => (
  <TooltipProvider delayDuration={200}>
    <Tooltip>
      <TooltipTrigger asChild>
        <Button size="icon" variant="ghost"
          className={`h-5 w-5 shrink-0 rounded-sm ${expanded ? "text-primary hover:text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground"}`}
          onClick={(e) => { e.stopPropagation(); onToggle(); }} aria-expanded={expanded}
          aria-label={`${expanded ? "Hide" : "Show"} Previous and Change columns for ${metric.label}`}
          data-testid={`supervisor-report-metric-toggle-${metric.key}`} data-state={expanded ? "expanded" : "collapsed"}>
          {expanded ? <ChevronsRightLeft className="h-3.5 w-3.5" /> : <ChevronsLeftRight className="h-3.5 w-3.5" />}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top">{expanded ? "Hide Previous & Change" : "Show Previous & Change"}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const ExportIcon = ({ onClick, title, testId, busy }) => (
  <TooltipProvider delayDuration={200}>
    <Tooltip>
      <TooltipTrigger asChild>
        <Button size="icon" variant="ghost" className="h-7 w-7" disabled={busy} data-testid={testId} aria-label={title}
          onClick={(e) => { e.stopPropagation(); onClick(); }}>
          <Download className="h-3.5 w-3.5" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="left">{title}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

/** Phase 181 — collectors + every receipt behind one ward / mohalla; loaded when the row is opened. */
const DetailPanel = ({ code, params, wardId, mohallaId, title, canExport, colSpan, testId }) => {
  const [state, setState] = useState({ loading: true, error: "", data: null });
  const [exporting, setExporting] = useState(false);
  const query = useMemo(() => ({ ...params, in_ward_id: wardId, ...(mohallaId ? { in_mohalla_id: mohallaId } : {}) }), [params, wardId, mohallaId]);
  const load = useCallback(async () => {
    setState({ loading: true, error: "", data: null });
    try {
      const r = await api.get(`/t/${code}/reports/supervisor-report/records`, { params: query });
      setState({ loading: false, error: "", data: r.data });
    } catch (e) {
      setState({ loading: false, error: errMsg(e), data: null });
    }
  }, [code, query]);
  useEffect(() => { load(); }, [load]);
  const exportReceipts = async () => {
    setExporting(true);
    try {
      const qs = new URLSearchParams(Object.entries(query).filter(([, v]) => v !== undefined && v !== null && v !== "")).toString();
      await downloadBlob(`/t/${code}/reports/supervisor-report/records/export?${qs}`, "receipts.xlsx");
      toast.success("Receipts exported", { description: title });
    } catch (e) {
      toast.error("Export failed", { description: errMsg(e) });
    } finally {
      setExporting(false);
    }
  };
  const d = state.data;
  return (
    <TableRow className="hover:bg-transparent" data-testid={testId}>
      <TableCell colSpan={colSpan} className="p-0">
        <div className="mx-4 my-3 rounded-lg border bg-card shadow-sm" onClick={(e) => e.stopPropagation()}>
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 border-b">
            <div>
              <p className="text-sm font-medium">{title}</p>
              <p className="text-xs text-muted-foreground">
                {d ? <>{n(d.counts.collectors)} collector{d.counts.collectors === 1 ? "" : "s"} · {n(d.counts.receipts)} receipt{d.counts.receipts === 1 ? "" : "s"} · {fmtINR(d.totals.collections_amount)}</> : "Who did the work and which receipts make up the collections"}
              </p>
            </div>
            {canExport && d && d.counts.receipts > 0 && (
              <Button size="sm" variant="outline" onClick={exportReceipts} disabled={exporting} data-testid={`${testId}-export`}>
                <FileSpreadsheet className="h-4 w-4 mr-1.5" />{exporting ? "Exporting…" : "Export receipts"}
              </Button>
            )}
          </div>
          {state.loading ? (
            <div className="p-4 space-y-2" data-testid={`${testId}-loading`}>{[0, 1, 2].map((i) => <Skeleton key={i} className="h-4" />)}</div>
          ) : state.error ? (
            <div className="p-4 text-sm" data-testid={`${testId}-error`}>
              <span className="text-destructive">{state.error}</span>
              <Button size="sm" variant="link" onClick={load}>Try again</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-5 gap-0 divide-y xl:divide-y-0 xl:divide-x">
              <div className="xl:col-span-2">
                <p className="px-4 pt-3 pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Collectors</p>
                {d.collectors.length === 0 ? (
                  <p className="px-4 pb-4 text-sm text-muted-foreground">No activity in this period.</p>
                ) : (
                  <Table data-testid={`${testId}-collectors`}>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Collector</TableHead>
                        <TableHead className="text-xs text-right">Visits</TableHead>
                        <TableHead className="text-xs text-right">Collections</TableHead>
                        <TableHead className="text-xs text-right">New</TableHead>
                        <TableHead className="text-xs text-right">Reassess.</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {d.collectors.map((c) => (
                        <TableRow key={c.key} data-testid={`${testId}-collector-${c.key}`}>
                          <TableCell className="text-sm">
                            <div className={c.kind === "bucket" ? "italic text-muted-foreground" : "font-medium"}>{c.name}</div>
                            <div className="text-[11px] text-muted-foreground">{c.role_label}{c.sub_users?.length ? ` · incl. ${c.sub_users.join(", ")}` : ""}</div>
                          </TableCell>
                          <TableCell className="text-right text-sm"><Metric value={c.visits} /></TableCell>
                          <TableCell className="text-right text-sm"><Money amount={c.collections_amount} count={c.collections_count} /></TableCell>
                          <TableCell className="text-right text-sm"><Metric value={c.new_assessments} /></TableCell>
                          <TableCell className="text-right text-sm"><Metric value={c.reassessments} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
              <div className="xl:col-span-3">
                <p className="px-4 pt-3 pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Receipts ({n(d.counts.receipts)})</p>
                {d.receipts.length === 0 ? (
                  <p className="px-4 pb-4 text-sm text-muted-foreground">No receipts in this period.</p>
                ) : (
                  <div className="max-h-80 overflow-y-auto" data-testid={`${testId}-receipts`}>
                    <Table>
                      <TableHeader className="sticky top-0 bg-card z-10">
                        <TableRow>
                          <TableHead className="text-xs">Date</TableHead>
                          <TableHead className="text-xs">Receipt</TableHead>
                          <TableHead className="text-xs">PIN · Owner</TableHead>
                          <TableHead className="text-xs">Collected by</TableHead>
                          <TableHead className="text-xs">Mode</TableHead>
                          <TableHead className="text-xs text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {d.receipts.map((r) => (
                          <TableRow key={r.id} data-testid={`${testId}-receipt-${r.id}`}>
                            <TableCell className="text-xs whitespace-nowrap">{fmtDate(r.date)}{r.time ? <span className="text-muted-foreground"> {r.time}</span> : null}</TableCell>
                            <TableCell className="text-xs font-mono">{r.receipt_number}</TableCell>
                            <TableCell className="text-xs"><span className="font-mono">{r.pin || "—"}</span>{r.owner_name ? <span className="text-muted-foreground"> · {r.owner_name}</span> : null}</TableCell>
                            <TableCell className="text-xs">{r.collected_by}<div className="text-[10px] text-muted-foreground">{r.channel}</div></TableCell>
                            <TableCell className="text-xs">{r.payment_mode}</TableCell>
                            <TableCell className="text-xs text-right tabular-nums font-medium">{fmtINR(r.amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

export default function SupervisorReport() {
  const { code } = useParams();
  const access = useRoleModules(code);
  const canExport = access.canWrite("reports", "supervisor_report");
  const masters = useTerritoryMasters(code);

  const [range, setRange] = useState(() => presetRange("this_month"));
  const [territory, setTerritory] = useState(EMPTY_TERRITORY);
  const [compare, setCompare] = useState(true);
  const [expandedCmp, setExpandedCmp] = useState(ALL_COLLAPSED); // Phase 182: metric key -> Previous/Change columns shown?
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openSup, setOpenSup] = useState({});       // row key -> bool
  const [openWard, setOpenWard] = useState({});     // `${rowKey}|${wardId}` -> bool
  const [openDetail, setOpenDetail] = useState({}); // `${rowKey}|${wardId}` or `${rowKey}|${wardId}|${mohallaKey}` -> bool
  const [exporting, setExporting] = useState("");
  const reqSeq = useRef(0);

  const baseParams = useMemo(() => ({ date_from: range.from, date_to: range.to, ...territoryParams(territory) }), [range, territory]);
  const params = useMemo(() => ({ ...baseParams, compare }), [baseParams, compare]);

  const load = useCallback(async () => {
    const seq = ++reqSeq.current;
    setLoading(true);
    setError("");
    try {
      const r = await api.get(`/t/${code}/reports/supervisor-report`, { params });
      if (seq !== reqSeq.current) return;   // a newer filter change already fired
      setData(r.data);
    } catch (e) {
      if (seq !== reqSeq.current) return;
      setError(errMsg(e));
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, [code, params]);

  // real-time filters: every change re-queries (short debounce so a multi-select burst fires once)
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);
  // detail panels belong to a period / area — close them when the filters move
  useEffect(() => { setOpenDetail({}); }, [baseParams]);

  const qs = (extra = {}) => new URLSearchParams(Object.entries({ ...params, ...extra }).filter(([, v]) => v !== undefined && v !== null && v !== "")).toString();
  const exportLevel = async (key, extra, label) => {
    setExporting(key);
    try {
      await downloadBlob(`/t/${code}/reports/supervisor-report/export?${qs(extra)}`, "supervisor_report.xlsx");
      toast.success(`${label} exported`, { description: rangeLabel(range) });
    } catch (e) {
      toast.error("Export failed", { description: errMsg(e) });
    } finally {
      setExporting("");
    }
  };

  const rows = data?.rows || [];
  const totals = data?.totals || {};
  const prevLabel = data?.compare?.label;
  const areaText = territoryLabel(masters, territory);
  const toggleSup = (k) => setOpenSup((s) => ({ ...s, [k]: !s[k] }));
  const toggleWard = (k) => setOpenWard((s) => ({ ...s, [k]: !s[k] }));
  const toggleDetail = (k) => setOpenDetail((s) => ({ ...s, [k]: !s[k] }));
  const allOpen = rows.length > 0 && rows.every((r) => openSup[r.key]);
  const setAll = (open) => {
    setOpenSup(Object.fromEntries(rows.map((r) => [r.key, open])));
    if (!open) { setOpenWard({}); setOpenDetail({}); }
  };
  // Phase 182 — a metric group shows Previous + Change only while compare is on AND its header toggle is expanded
  const isExpanded = (key) => compare && !!expandedCmp[key];
  const toggleCmp = (key) => setExpandedCmp((s) => ({ ...s, [key]: !s[key] }));
  const expandedCount = compare ? METRICS.filter((m) => expandedCmp[m.key]).length : 0;
  const allCmpOpen = expandedCount === METRICS.length;
  const metricCols = 4 + expandedCount * 2;
  const colSpan = 2 + metricCols + (canExport ? 1 : 0);

  return (
    <div data-testid="supervisor-report-page">
      <PageHeader title="Supervisor Report"
        subtitle="Visits, collections, new assessments and reassessments by supervisor — every ward mapped to them, drill down to ward, mohalla and collector">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="supervisor-report-refresh">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />Refresh
          </Button>
          {canExport && (
            <Button size="sm" onClick={() => exportLevel("all", { level: "all" }, "Full report")} disabled={loading || !!exporting || !rows.length}
              data-testid="supervisor-report-export-all">
              <FileSpreadsheet className="h-4 w-4 mr-1.5" />{exporting === "all" ? "Exporting…" : "Export full report"}
            </Button>
          )}
        </div>
      </PageHeader>

      {/* ------------------------------------------------------------------ filters (live) */}
      <Card className="mb-4" data-testid="supervisor-report-filters">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <DateRangePicker value={range} onChange={setRange} testIdPrefix="supervisor-report-range" />
            <div className="flex items-center gap-2 pb-1" data-testid="supervisor-report-compare">
              <Switch id="supervisor-report-compare-switch" checked={compare} onCheckedChange={setCompare} data-testid="supervisor-report-compare-switch" />
              <Label htmlFor="supervisor-report-compare-switch" className="text-sm cursor-pointer">
                Compare with previous period
                {compare && prevLabel && (
                  <span className="block text-[11px] text-muted-foreground font-normal" data-testid="supervisor-report-compare-label">
                    Previous = {prevLabel} · use the <ChevronsLeftRight className="inline h-3 w-3 align-[-2px]" aria-hidden="true" /> icon beside a metric header to show its Previous & Change columns
                  </span>
                )}
              </Label>
            </div>
          </div>
          <TerritoryFilterSelects masters={masters} value={territory} onChange={setTerritory} testIdPrefix="supervisor-report-area" compact />
          <p className="text-xs text-muted-foreground" data-testid="supervisor-report-filter-summary">
            Showing <span className="font-medium text-foreground">{rangeLabel(range)}</span>
            {areaText ? <> · <span className="font-medium text-foreground">{areaText}</span></> : " · whole municipality"}
            {" · "}figures come from each ward's own properties (citizen payments and imports included), updating as you change filters.
          </p>
        </CardContent>
      </Card>

      {/* ------------------------------------------------------------------ KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {METRICS.map((m) => {
          const Icon = { visits: Footprints, collections_amount: IndianRupee, new_assessments: ClipboardList, reassessments: Users2 }[m.key];
          const ch = totals.change?.[m.key];
          const sub = m.money ? `${n(totals.collections_count)} receipts` : m.key === "reassessments" && data
            ? `${n(data.counts?.supervisors)} supervisor${data.counts?.supervisors === 1 ? "" : "s"} · ${n(data.counts?.wards)} ward${data.counts?.wards === 1 ? "" : "s"}` : undefined;
          const cmp = compare && totals.prev ? `prev ${m.money ? fmtINR(totals.prev.collections_amount) : n(totals.prev[m.key])}${ch ? ` (${ch.abs > 0 ? "+" : ""}${ch.pct == null ? (ch.abs ? "new" : "0%") : `${ch.pct}%`})` : ""}` : "";
          return <KpiCard key={m.key} label={m.label} value={totals[m.key] ?? 0} money={m.money} icon={Icon} sub={[sub, cmp].filter(Boolean).join(" · ") || undefined}
            testId={`supervisor-report-kpi-${{ visits: "visits", collections_amount: "collections", new_assessments: "new", reassessments: "reassessments" }[m.key]}`} />;
        })}
      </div>

      {/* ------------------------------------------------------------------ table */}
      <Card>
        <CardContent className="p-0">
          <div className="flex items-center justify-between px-4 py-2 border-b">
            <p className="text-xs text-muted-foreground">
              Click a supervisor for its wards, a ward for its mohallas, a mohalla (or a ward without mohallas) for the collectors and receipts behind the figures.
              Rows labelled <span className="font-medium">Unassigned (Zone Placeholder)</span> hold the wards of a zone that no supervisor owns.
              {compare && <> The <ChevronsLeftRight className="inline h-3 w-3 align-[-2px]" aria-hidden="true" /> icon beside a metric header shows or hides its Previous & Change columns at every level.</>}
            </p>
            {rows.length > 0 && (
              <div className="flex items-center gap-1 shrink-0">
                {compare && (
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setExpandedCmp(allCmpOpen ? ALL_COLLAPSED : ALL_EXPANDED)}
                    data-testid="supervisor-report-toggle-all-comparisons" aria-pressed={allCmpOpen}>
                    {allCmpOpen ? <ChevronsRightLeft className="h-3.5 w-3.5 mr-1" /> : <ChevronsLeftRight className="h-3.5 w-3.5 mr-1" />}
                    {allCmpOpen ? "Hide all comparisons" : "Show all comparisons"}
                  </Button>
                )}
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setAll(!allOpen)} data-testid="supervisor-report-toggle-all">
                  {allOpen ? "Collapse all" : "Expand all"}
                </Button>
              </div>
            )}
          </div>
          {error ? (
            <div className="p-6 text-center" data-testid="supervisor-report-error">
              <AlertTriangle className="h-6 w-6 text-destructive mx-auto mb-2" />
              <p className="text-sm font-medium">Could not load the report</p>
              <p className="text-xs text-muted-foreground mt-1">{error}</p>
              <Button size="sm" variant="outline" className="mt-3" onClick={load}>Try again</Button>
            </div>
          ) : loading && !data ? (
            <TableRowsSkeleton rows={6} cols={6} testId="supervisor-report-skeleton" />
          ) : rows.length === 0 ? (
            <EmptyState title="No supervisors or wards match these filters"
              subtitle="Widen the zone / ward / mohalla selection or the date range. Supervisors appear once they are mapped to at least one ward." />
          ) : (
            <div className={`overflow-x-auto ${loading ? "opacity-60 transition-opacity" : ""}`} aria-busy={loading}>
              <Table data-testid="supervisor-report-table" className={MIN_W[expandedCount]} data-expanded-groups={expandedCount}>
                <TableHeader>
                  {compare ? (
                    <>
                      <TableRow className="hover:bg-transparent" data-testid="supervisor-report-header-groups">
                        <TableHead rowSpan={2} className="w-[130px] align-bottom">Zone</TableHead>
                        <TableHead rowSpan={2} className="align-bottom">Supervisor</TableHead>
                        {METRICS.map((m) => {
                          const ex = isExpanded(m.key);
                          return (
                            <TableHead key={m.key} colSpan={ex ? 3 : 1} className="text-center border-l border-b-0 pb-0 text-foreground whitespace-nowrap"
                              data-testid={`supervisor-report-metric-group-${m.key}`} data-state={ex ? "expanded" : "collapsed"}>
                              <div className="flex items-center justify-center gap-1">
                                <span>{m.label}</span>
                                <MetricToggle metric={m} expanded={ex} onToggle={() => toggleCmp(m.key)} />
                              </div>
                            </TableHead>
                          );
                        })}
                        {canExport && <TableHead rowSpan={2} className="w-[48px] align-bottom"><span className="sr-only">Export</span></TableHead>}
                      </TableRow>
                      <TableRow className="hover:bg-transparent" data-testid="supervisor-report-header-sub">
                        {METRICS.map((m) => (
                          <React.Fragment key={m.key}>
                            <TableHead className="text-right text-[11px] border-l h-8" data-testid={`supervisor-report-subhead-${m.key}-current`}>Current</TableHead>
                            {isExpanded(m.key) && (
                              <>
                                <TableHead className="text-right text-[11px] h-8 bg-muted/20" title={prevLabel ? `Previous period: ${prevLabel}` : undefined}
                                  data-testid={`supervisor-report-subhead-${m.key}-prev`}>Previous</TableHead>
                                <TableHead className="text-right text-[11px] h-8" data-testid={`supervisor-report-subhead-${m.key}-change`}>Change</TableHead>
                              </>
                            )}
                          </React.Fragment>
                        ))}
                      </TableRow>
                    </>
                  ) : (
                    <TableRow>
                      <TableHead className="w-[140px]">Zone</TableHead>
                      <TableHead>Supervisor</TableHead>
                      {METRICS.map((m) => <TableHead key={m.key} className="text-right">{m.label}</TableHead>)}
                      {canExport && <TableHead className="w-[48px]"><span className="sr-only">Export</span></TableHead>}
                    </TableRow>
                  )}
                </TableHeader>
                <TableBody>
                  {rows.map((r) => {
                    const open = !!openSup[r.key];
                    const expandable = r.wards.length > 0;
                    return (
                      <React.Fragment key={r.key}>
                        <TableRow className={`cursor-pointer select-none ${open ? "bg-muted/40" : ""} ${r.kind !== "supervisor" ? "text-muted-foreground" : ""}`}
                          onClick={() => expandable && toggleSup(r.key)} data-testid={`supervisor-report-row-${r.key}`} data-state={open ? "open" : "closed"}
                          aria-expanded={expandable ? open : undefined}>
                          <TableCell className="text-sm">{r.zone_name}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Caret open={open} hidden={!expandable} />
                              <div className="min-w-0">
                                <div className={`font-medium ${r.kind === "supervisor" ? "text-foreground" : "italic"}`} data-testid={`supervisor-report-name-${r.key}`}>
                                  {r.name}{r.employee_id ? <span className="text-muted-foreground font-normal"> ({r.employee_id})</span> : null}
                                </div>
                                <div className="text-[11px] text-muted-foreground">
                                  {r.ward_count} ward{r.ward_count === 1 ? "" : "s"}
                                  {r.kind === "unassigned" && " · no supervisor mapped"}
                                  {r.kind === "no_ward" && " · properties without a ward"}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          {METRICS.map((m) => <MetricCells key={m.key} node={r} metric={m} expanded={isExpanded(m.key)} testPrefix={`supervisor-report-${r.key}`} />)}
                          {canExport && (
                            <TableCell className="text-right">
                              {expandable && <ExportIcon title={`Export ${r.name} (ward-wise)`} busy={!!exporting} testId={`supervisor-report-export-${r.key}`}
                                onClick={() => exportLevel(r.key, { level: "supervisor", supervisor_key: r.key }, r.name)} />}
                            </TableCell>
                          )}
                        </TableRow>

                        {open && r.wards.map((w) => {
                          const wk = `${r.key}|${w.ward_id}`;
                          const hasMoh = w.mohallas.length > 0;
                          const wopen = hasMoh ? !!openWard[wk] : !!openDetail[wk];
                          return (
                            <React.Fragment key={wk}>
                              <TableRow className={`cursor-pointer select-none bg-muted/20 ${wopen ? "bg-muted/50" : ""}`}
                                onClick={() => (hasMoh ? toggleWard(wk) : toggleDetail(wk))} data-testid={`supervisor-report-wardrow-${wk}`} data-state={wopen ? "open" : "closed"}
                                aria-expanded={wopen}>
                                <TableCell className="text-xs text-muted-foreground">{w.zone_name}</TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2 pl-6">
                                    <Caret open={wopen} />
                                    <span className="text-sm">{w.ward_name}</span>
                                    {w.shared && (
                                      <TooltipProvider delayDuration={200}>
                                        <Tooltip>
                                          <TooltipTrigger asChild><Badge variant="outline" className="text-[10px] h-4 px-1.5">shared</Badge></TooltipTrigger>
                                          <TooltipContent>This ward is covered by more than one supervisor; its figures appear under each of them.</TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    )}
                                    {!hasMoh && <span className="text-[11px] text-muted-foreground">· no mohallas — click for collectors & receipts</span>}
                                  </div>
                                </TableCell>
                                {METRICS.map((m) => <MetricCells key={m.key} node={w} metric={m} expanded={isExpanded(m.key)} size="text-sm" testPrefix={`supervisor-report-ward-${wk}`} />)}
                                {canExport && (
                                  <TableCell className="text-right">
                                    {hasMoh && <ExportIcon title={`Export ${w.ward_name} (mohalla-wise)`} busy={!!exporting} testId={`supervisor-report-export-wardrow-${wk}`}
                                      onClick={() => exportLevel(wk, { level: "ward", supervisor_key: r.key, in_ward_id: w.ward_id }, `${r.name} · ${w.ward_name}`)} />}
                                  </TableCell>
                                )}
                              </TableRow>
                              {!hasMoh && openDetail[wk] && (
                                <DetailPanel code={code} params={baseParams} wardId={w.ward_id} mohallaId={null} canExport={canExport} colSpan={colSpan}
                                  title={`${w.zone_name} › ${w.ward_name} · ${rangeLabel(range)}`} testId={`supervisor-report-detail-${wk}`} />
                              )}
                              {hasMoh && openWard[wk] && w.mohallas.map((m) => {
                                const mk = `${wk}|${m.key}`;
                                const dopen = !!openDetail[mk];
                                return (
                                  <React.Fragment key={mk}>
                                    <TableRow className={`cursor-pointer select-none bg-muted/10 ${dopen ? "bg-muted/40" : ""}`} onClick={() => toggleDetail(mk)}
                                      data-testid={`supervisor-report-mohallarow-${mk}`} data-state={dopen ? "open" : "closed"} aria-expanded={dopen}>
                                      <TableCell />
                                      <TableCell>
                                        <div className="flex items-center gap-2 pl-12">
                                          <Caret open={dopen} />
                                          <span className={`text-sm ${m.foreign ? "text-amber-700 dark:text-amber-400" : "text-muted-foreground"}`}
                                            title={m.foreign ? "Properties in this ward carry a mohalla that Master Data links to another ward (stale area link). Master Data → Wards → Check area links can repair it." : undefined}>
                                            {m.name}
                                          </span>
                                        </div>
                                      </TableCell>
                                      {METRICS.map((mm) => <MetricCells key={mm.key} node={m} metric={mm} expanded={isExpanded(mm.key)} size="text-sm" testPrefix={`supervisor-report-mohalla-${mk}`} />)}
                                      {canExport && <TableCell />}
                                    </TableRow>
                                    {dopen && (
                                      <DetailPanel code={code} params={baseParams} wardId={w.ward_id} mohallaId={m.key} canExport={canExport} colSpan={colSpan}
                                        title={`${w.zone_name} › ${w.ward_name} › ${m.name} · ${rangeLabel(range)}`} testId={`supervisor-report-detail-${mk}`} />
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                  <TableRow className="font-semibold bg-muted/30" data-testid="supervisor-report-total-row">
                    <TableCell colSpan={2}>Total <span className="text-xs text-muted-foreground font-normal">(each ward counted once)</span></TableCell>
                    {METRICS.map((m) => <MetricCells key={m.key} node={totals} metric={m} expanded={isExpanded(m.key)} testPrefix="supervisor-report-total" />)}
                    {canExport && <TableCell />}
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
